import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { HoneybookAI, IHoneybookAI } from '../ai-core';
import {
    useHttpClient,
    useLogger,
    useStreamingProviderFactory,
    useStorage,
    useAppLifecycle
} from '~/packages/core-react';
import { HoneybookAISingletonContext } from './honeybook-ai-singleton-provider';

const HoneybookAIContext = createContext<HoneybookAI>(null);

// Provider props interface
export interface HoneybookAIProviderProps {
    honeybookAIKey?: string;
    children: React.ReactNode;
}

export const HoneybookAIProvider = ({
    honeybookAIKey,
    children
}: HoneybookAIProviderProps) => {
    const httpClient = useHttpClient();
    const logger = useLogger();
    const streamingProviderFactory = useStreamingProviderFactory();
    const storage = useStorage();
    const appLifecycleProvider = useAppLifecycle();

    // Create HoneybookAI instance once and memoize it
    const ai = useMemo(() => {
        return new HoneybookAI({
            httpClient,
            streamingProviderFactory,
            logger,
            storage,
            appLifecycleProvider
        });
        // eslint-disable-next-line  react-hooks/exhaustive-deps
    }, [
        httpClient,
        streamingProviderFactory,
        logger,
        storage,
        appLifecycleProvider,
        honeybookAIKey
    ]);

    return (
        <HoneybookAIContext.Provider value={ai}>
            {children}
        </HoneybookAIContext.Provider>
    );
};

/**
 * Hook to access the HoneybookAI instance from the context.
 * Triggers lazy initialization on first usage.
 * @returns The HoneybookAI instance
 * @throws Error if used outside of a HoneybookAIProvider
 */
export const useHoneybookAI = (): IHoneybookAI => {
    const { ai: singletonAI } = useContext(HoneybookAISingletonContext);
    const ai = useContext(HoneybookAIContext);
    const logger = useLogger();

    if (!singletonAI && !ai) {
        throw new Error(
            'useHoneybookAI must be used within a HoneybookAIProvider'
        );
    }

    const honeybookAI = singletonAI || ai;

    // Lazy initialization on first useHoneybookAI call (M3)
    useEffect(() => {
        honeybookAI
            .getTaskRepository()
            .initialize()
            .catch(error => {
                logger.error('HoneybookAI initialization failed:', error);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [honeybookAI]);

    return honeybookAI;
};
